import React from 'react';
import { motion } from 'framer-motion';
import { Database, Tag, Layers, Eye } from 'lucide-react';
import { StatCard } from '../../../shared/StatCard';
import { Card } from '../../../../components/ui/Card';
import { Badge } from '../../../../components/ui/Badge';
import { Button } from '../../../../components/ui/Button';
import { containerVariants, itemVariants } from '../../shared/animationVariants';
interface SubjectData {
  code: string;
  name: string;
  questionCount: number;
  familyCount: number;
  color: string;
  avgTier: number;
}
interface OverviewTabProps {
  subjects: SubjectData[];
  totalQuestions: number;
  totalFamilies: number;
  typeCount?: number;
  visualCount?: number;
  onBrowseSubject: (code: string) => void;
}
export function OverviewTab({
  subjects,
  totalQuestions,
  totalFamilies,
  typeCount = 0,
  visualCount = 0,
  onBrowseSubject
}: OverviewTabProps) {
  const activeSubjects = subjects.filter(s => s.questionCount > 0).length;
  return <div className="space-y-6" data-id="element-6134" data-mp="3hlv9k0f2osc8">
      {/* Stats Row */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-id="element-6135" data-mp="3hnof1cdy96in">
        
        <motion.div variants={itemVariants} data-id="element-6136" data-mp="38l6xvcd5j42i">
          <StatCard label="Totaal Vragen" value={totalQuestions.toLocaleString('nl-NL')} icon={<Database className="w-5 h-5" data-id="element-6138" data-mp="33nnho6ww8h7x" />} color="blue" data-id="element-6137" data-mp="38lypkc3mohni" />
          
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-6139" data-mp="53onax5zviye">
          <StatCard label="Families" value={totalFamilies} icon={<Tag className="w-5 h-5" data-id="element-6141" data-mp="6ogul58ge6r" />} color="purple" data-id="element-6140" data-mp="56t2sqowu14d" />
          
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-6142" data-mp="3rjj5gtof05vz">
          <StatCard label="Vraagtypen" value={typeCount} icon={<Layers className="w-5 h-5" data-id="element-6144" data-mp="3mlzrluvmpv4k" />} color="emerald" data-id="element-6143" data-mp="3rigc8f3mdrcw" />
          
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-6145" data-mp="nvpv4p0qyjjy">
          <StatCard label="Actieve Vakken" value={`${activeSubjects} / ${subjects.length}`} icon={<Eye className="w-5 h-5" data-id="element-6147" data-mp="j3pralhk36i3" />} color="amber" data-id="element-6146" data-mp="nwisx0hq9ypy" />
          
        </motion.div>
      </motion.div>

      {/* Subject Cards Grid */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-id="element-6148" data-mp="2depi0xvhdq9u">
        
        {subjects.map(subject => {
        const isActive = subject.questionCount > 0;
        return <motion.div key={subject.code} variants={itemVariants} data-id="element-6149" data-mp="28k20jiyw9w4r">
              <Card className={`p-5 h-full flex flex-col transition-all duration-200 cursor-pointer border border-gray-200 ${isActive ? 'hover:shadow-md hover:border-blue-300' : 'opacity-50 bg-gray-50/50 cursor-not-allowed'}`} onClick={() => isActive && onBrowseSubject(subject.code)} data-id="element-6150" data-mp="1to2v6g366a3b">
                
                {/* Header */}
                <div className="flex items-start justify-between mb-4" data-id="element-6151" data-mp="1pnaitdz03p2t">
                  <div className="flex items-center gap-3" data-id="element-6152" data-mp="3f2qhfzfatseg">
                    <div className={`w-11 h-11 rounded-xl ${subject.color} flex items-center justify-center shadow-sm`} data-id="element-6153" data-mp="3k0ryyfx2tjnt">
                      
                      <span className="text-white font-black text-sm" data-id="element-6154" data-mp="3u5i6r2f5vhzy">
                        {subject.code.slice(0, 2)}
                      </span>
                    </div>
                    <div className="min-w-0" data-id="element-6155" data-mp="6s65dwb9wdgq">
                      <h3 className="font-bold text-gray-900 text-sm truncate" data-id="element-6156" data-mp="6sxpoazf6orp">
                        {subject.name}
                      </h3>
                      <code className="text-[10px] text-gray-500 font-mono bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-6157" data-mp="gparjw54c8xj">
                        {subject.code}
                      </code>
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="space-y-2.5 mb-4 flex-1" data-id="element-6158" data-mp="lox4s5ky5p6c">
                  <div className="flex items-center justify-between" data-id="element-6159" data-mp="lpez2wo1ttbm">
                    <span className="text-xs text-gray-500" data-id="element-6160" data-mp="2rh3lwqqzvnfg">Vragen</span>
                    <span className={`text-sm font-bold tabular-nums ${isActive ? 'text-gray-900' : 'text-gray-400'}`} data-id="element-6161" data-mp="2ng9zk1t7agoo">
                      
                      {subject.questionCount.toLocaleString('nl-NL')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-6162" data-mp="2tup8p9rahd1i">
                    <span className="text-xs text-gray-500" data-id="element-6163" data-mp="2xz6sh8xnfqca">Families</span>
                    <span className="text-sm font-medium text-gray-700 tabular-nums" data-id="element-6164" data-mp="32xqef9c8t24n">
                      {subject.familyCount}
                    </span>
                  </div>
                  {isActive && subject.avgTier > 0 && <div className="flex items-center justify-between" data-id="element-6165" data-mp="2a7rn17aksswt">
                      <span className="text-xs text-gray-500" data-id="element-6166" data-mp="2f7w2ctzqzhlg">Gem. Tier</span>
                      <Badge variant="neutral" size="sm" data-id="element-6167" data-mp="2f5t2li8sma3j">
                        {subject.avgTier}
                      </Badge>
                    </div>}
                </div>

                {/* Distribution bar */}
                {isActive && <div className="mb-4" data-id="element-6168" data-mp="1rhjlgtmiyaiq">
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-6169" data-mp="1rggxouki0o71">
                      <motion.div initial={{
                  width: 0
                }} animate={{
                  width: `${Math.min(100, subject.questionCount / Math.max(totalQuestions, 1) * 100)}%`
                }} transition={{
                  duration: 0.6,
                  delay: 0.1
                }} className={`h-full rounded-full ${subject.color}`} data-id="element-6170" data-mp="1jx7mqwwcghfv" />
                    
                    </div>
                    <div className="text-[10px] text-gray-400 mt-1 text-right" data-id="element-6171" data-mp="110zdzj44hoft">
                      {Math.round(subject.questionCount / Math.max(totalQuestions, 1) * 100)}
                      % van totaal
                    </div>
                  </div>}

                {/* Action */}
                <div className="mt-auto pt-3 border-t border-gray-100" data-id="element-6172" data-mp="mcd07lu594qp">
                  <Button variant="secondary" size="sm" className="w-full justify-center" onClick={() => isActive && onBrowseSubject(subject.code)} disabled={!isActive} data-id="element-6173" data-mp="i9gdpk1hgohh">
                    
                    <Eye className="w-4 h-4 mr-1.5" data-id="element-6174" data-mp="3lycwwr1ke0c" />
                    Bekijk {subject.questionCount} vragen
                  </Button>
                </div>
              </Card>
            </motion.div>;
      })}
      </motion.div>
    </div>;
}